import axios from "axios";
import { getAuthHeaders } from "../../../utils/authUtils";
import { API_BASE_URL } from "../../../environment";

export interface ProactiveSaleAlertRule {
  proactive_sale_alert_rule_id: number;
  rule_name: string;
  rule_description: string;
  is_active: boolean;
  schedule_expression: string | null;
  timezone: string | null;
  last_execution_datetime: string | null;
  next_execution_datetime: string | null;
  created_datetime: string;
  updated_datetime: string;
  is_ai_enabled: boolean;
  tenant_id: number;
  created_by: number;
  updated_by: number;
  crm_entity: {
    crm_entity_id: number;
    crm_entity_key: string;
    crm_entity_display_name: string;
  };
  trigger_type: {
    trigger_type_id: number;
    trigger_type_key: string;
    trigger_type_display_name: string;
  };
  event: {
    event_id: number;
    event_key: string;
    event_display_name: string;
  };
}

export interface PaginatedResponse<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

export interface CrmEntity {
  crm_entity_id: number;
  crm_entity_key: string;
  crm_entity_display_name: string;
  is_portal_active: boolean;
  is_open_api_active: boolean;
  is_open_api_enabled: boolean;
}

export interface TriggerType {
  trigger_type_id: number;
  trigger_type_key: string;
  trigger_type_display_name: string;
  is_active: boolean;
}

export interface EventType {
  event_id: number;
  event_key: string;
  event_display_name: string;
  is_active: boolean;
  is_internal: boolean;
  crm_entity_id: number;
  tenant_id: number;
  created_by: number;
  updated_by: number;
}

export interface EntityField {
  field_name: string;
  display_name: string;
  data_type: string;
  allowed_operators: string[];
}

export interface ConditionOperator {
  condition_operator_id: number;
  condition_operator_key: string;
  condition_operator_display_name: string;
  description: string;
  is_active: boolean;
}

export interface FrequencyConstant {
  value: string;
  label: string;
}

export interface TimingConstant {
  value: string;
  label: string;
}

export interface WeekDay {
  week_day_id: number;
  week_day_key: string;
  week_day_display_name: string;
}

export interface LifecycleSource {
  lifecycle_source_id: number;
  lifecycle_source_key: string;
  lifecycle_source_display_name: string;
  source_order: number;
  is_active: boolean;
  created_datetime: string;
  updated_datetime: string;
  tenant_id: number;
  created_by: number | null;
  updated_by: number | null;
}

export const alertPolicyService = {
  getProactiveSaleAlertRules: async (crmEntityKey: string, tenantId?: string | number) => {
    let url = `${API_BASE_URL}/proactive-sale-alert-rule/?crm_entity_key=${crmEntityKey}`;
    if (tenantId) {
      url += `&tenant_id=${tenantId}`;
    }
    const response = await axios.get<PaginatedResponse<ProactiveSaleAlertRule>>(
      url,
      { headers: getAuthHeaders() }
    );
    return response.data;
  },
  getProactiveSaleAlertRule: async (id: number) => {
    const response = await axios.get(
      `${API_BASE_URL}/proactive-sale-alert-rule/${id}/`,
      { headers: getAuthHeaders() }
    );
    return response.data;
  },
  getProactiveSaleAlertRuleConditions: async (ruleId: number) => {
    const response = await axios.get(
      `${API_BASE_URL}/proactive-sale-alert-rule-condition/?proactive_sale_alert_rule_id=${ruleId}`,
      { headers: getAuthHeaders() }
    );
    return response.data;
  },
  getCrmEntities: async (crmEntityKey: string) => {
    const response = await axios.get<PaginatedResponse<CrmEntity>>(
      `${API_BASE_URL}/crm-entity/?crm_entity_key=${crmEntityKey}`,
      { headers: getAuthHeaders() }
    );
    return response.data;
  },
  getTriggerTypes: async () => {
    const response = await axios.get<PaginatedResponse<TriggerType>>(
      `${API_BASE_URL}/trigger-type/`,
      { headers: getAuthHeaders() }
    );
    return response.data;
  },
  getEvents: async (crmEntityId: number) => {
    const response = await axios.get<PaginatedResponse<EventType>>(
      `${API_BASE_URL}/event/?crm_entity_id=${crmEntityId}`,
      { headers: getAuthHeaders() }
    );
    return response.data;
  },
  getFrequencies: async () => {
    const response = await axios.get<FrequencyConstant[]>(
      `${API_BASE_URL}/lookup/constants/?module=frequency`,
      { headers: getAuthHeaders() }
    );
    return response.data;
  },
  getTimings: async () => {
    const response = await axios.get<TimingConstant[]>(
      `${API_BASE_URL}/lookup/constants/?module=timing`,
      { headers: getAuthHeaders() }
    );
    return response.data;
  },
  getWeekDays: async () => {
    const response = await axios.get<WeekDay[]>(
      `${API_BASE_URL}/week-day/`,
      { headers: getAuthHeaders() }
    );
    return response.data;
  },
  createProactiveSaleAlertRule: async (payload: any) => {
    const response = await axios.post(
      `${API_BASE_URL}/proactive-sale-alert-rule/`,
      payload,
      { headers: getAuthHeaders() }
    );
    return response.data;
  },
  updateProactiveSaleAlertRule: async (id: number, payload: any) => {
    const response = await axios.patch(
      `${API_BASE_URL}/proactive-sale-alert-rule/${id}/`,
      payload,
      { headers: getAuthHeaders() }
    );
    return response.data;
  },
  deleteProactiveSaleAlertRule: async (id: number) => {
    const response = await axios.delete(
      `${API_BASE_URL}/proactive-sale-alert-rule/${id}/`,
      { headers: getAuthHeaders() }
    );
    return response.data;
  },
  getEntityFields: async (entityName: string) => {
    const response = await axios.get(
      `${API_BASE_URL}/lookup/entity-field/?entity_name=${entityName}&is_from_alert=true`,
      { headers: getAuthHeaders() }
    );
    return response.data;
  },
  getConditionOperators: async () => {
    const response = await axios.get<PaginatedResponse<ConditionOperator>>(
      `${API_BASE_URL}/condition-operator/`,
      { headers: getAuthHeaders() }
    );
    return response.data;
  },
  createProactiveSaleAlertRuleCondition: async (payload: any) => {
    const response = await axios.post(
      `${API_BASE_URL}/proactive-sale-alert-rule-condition/`,
      payload,
      { headers: getAuthHeaders() }
    );
    return response.data;
  },
  deleteProactiveSaleAlertRuleCondition: async (id: number) => {
    const response = await axios.delete(
      `${API_BASE_URL}/proactive-sale-alert-rule-condition/${id}/`,
      { headers: getAuthHeaders() }
    );
    return response.data;
  },
  updateProactiveSaleAlertRuleCondition: async (id: number, payload: any) => {
    const response = await axios.patch(
      `${API_BASE_URL}/proactive-sale-alert-rule-condition/${id}/`,
      payload,
      { headers: getAuthHeaders() }
    );
    return response.data;
  },
  getProactiveSaleAlertAiContexts: async (crmEntityId: number) => {
    const response = await axios.get(
      `${API_BASE_URL}/proactive-sale-alert-ai-context/?crm_entity_id=${crmEntityId}`,
      { headers: getAuthHeaders() }
    );
    return response.data;
  },
  getProactiveSaleAlertAiTasks: async (crmEntityId: number) => {
    const response = await axios.get(
      `${API_BASE_URL}/proactive-sale-alert-ai-task/?crm_entity_id=${crmEntityId}`,
      { headers: getAuthHeaders() }
    );
    return response.data;
  },
  createProactiveSaleAlertRuleAiConfiguration: async (payload: any) => {
    const response = await axios.post(
      `${API_BASE_URL}/proactive-sale-alert-rule-ai-configuration/`,
      payload,
      { headers: getAuthHeaders() }
    );
    return response.data;
  },
  updateProactiveSaleAlertRuleAiConfiguration: async (id: number, payload: any) => {
    const response = await axios.patch(
      `${API_BASE_URL}/proactive-sale-alert-rule-ai-configuration/${id}/`,
      payload,
      { headers: getAuthHeaders() }
    );
    return response.data;
  },
  getProactiveSaleAlertRuleAiConfiguration: async (ruleId: number) => {
    const response = await axios.get(
      `${API_BASE_URL}/proactive-sale-alert-rule-ai-configuration/?proactive_sale_alert_rule_id=${ruleId}`,
      { headers: getAuthHeaders() }
    );
    return response.data;
  },
  getNotificationRecipientTypes: async () => {
    const response = await axios.get(
      `${API_BASE_URL}/notification-receipent-type/`,
      { headers: getAuthHeaders() }
    );
    return response.data;
  },
  getNotificationChannels: async () => {
    const response = await axios.get(
      `${API_BASE_URL}/notification-channel/`,
      { headers: getAuthHeaders() }
    );
    return response.data;
  },
  getProactiveSaleAlertRuleNotificationChannels: async (ruleId: number) => {
    const response = await axios.get(
      `${API_BASE_URL}/proactive-sale-alert-rule-notification-channel/?proactive_sale_alert_rule_id=${ruleId}`,
      { headers: getAuthHeaders() }
    );
    return response.data;
  },
  createProactiveSaleAlertRuleNotificationChannel: async (payload: any) => {
    const response = await axios.post(
      `${API_BASE_URL}/proactive-sale-alert-rule-notification-channel/`,
      payload,
      { headers: getAuthHeaders() }
    );
    return response.data;
  },
  updateProactiveSaleAlertRuleNotificationChannel: async (id: number, payload: any) => {
    const response = await axios.patch(
      `${API_BASE_URL}/proactive-sale-alert-rule-notification-channel/${id}/`,
      payload,
      { headers: getAuthHeaders() }
    );
    return response.data;
  },
  deleteProactiveSaleAlertRuleNotificationChannel: async (id: number) => {
    const response = await axios.delete(
      `${API_BASE_URL}/proactive-sale-alert-rule-notification-channel/${id}/`,
      { headers: getAuthHeaders() }
    );
    return response.data;
  },
  getProactiveSaleAlertRuleNotificationRecipients: async (ruleId: number) => {
    const response = await axios.get(
      `${API_BASE_URL}/proactive-sale-alert-rule-notification-recipient/?proactive_sale_alert_rule_id=${ruleId}`,
      { headers: getAuthHeaders() }
    );
    return response.data;
  },
  createProactiveSaleAlertRuleNotificationRecipient: async (payload: any) => {
    const response = await axios.post(
      `${API_BASE_URL}/proactive-sale-alert-rule-notification-recipient/`,
      payload,
      { headers: getAuthHeaders() }
    );
    return response.data;
  },
  updateProactiveSaleAlertRuleNotificationRecipient: async (id: number, payload: any) => {
    const response = await axios.patch(
      `${API_BASE_URL}/proactive-sale-alert-rule-notification-recipient/${id}/`,
      payload,
      { headers: getAuthHeaders() }
    );
    return response.data;
  },
  deleteProactiveSaleAlertRuleNotificationRecipient: async (id: number) => {
    const response = await axios.delete(
      `${API_BASE_URL}/proactive-sale-alert-rule-notification-recipient/${id}/`,
      { headers: getAuthHeaders() }
    );
    return response.data;
  },
  getLifecycleSources: async (tenantId: string | number) => {
    const response = await axios.get<PaginatedResponse<LifecycleSource>>(
      `${API_BASE_URL}/lifecycle-source/?tenant_id=${tenantId}`,
      { headers: getAuthHeaders() }
    );
    return response.data;
  }
};
